<template>
  <div
    class="w-full h-full flex items-center justify-center cursor-pointer"
    @click="handleTogglePlayback"
  >
    <template v-if="isVideo">
      <video v-show="isLoaded" ref="playerRef" controls class="h-[inherit]" />
      <ThumbnailIcon
        v-show="!isLoaded"
        :icon-size="320"
        :type="FileType.Video"
        showShadow
      />
    </template>
    <template v-if="isAudio">
      <ThumbnailIcon :icon-size="320" :type="FileType.Audio" showShadow />
      <audio ref="playerRef" controls class="w-full absolute bottom-0" />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { FileType } from '@/shared/graphql/legacy/generated'
import { ThumbnailIcon } from '@dacr/ui'
import { getTokens } from '@dacr/auth'

const props = defineProps<{
  type: FileType
  url?: string | null
}>()

const playerRef = ref<HTMLVideoElement | HTMLAudioElement | null>(null)
const isLoaded = ref(false)
const isPlaying = ref(false)

const isAudio = computed(() => props.type === FileType.Audio)
const isVideo = computed(() => props.type === FileType.Video)

const handleTogglePlayback = () => {
  if (!playerRef.value) return

  isPlaying.value = !isPlaying.value

  if (isPlaying.value) {
    playerRef.value.play()
  } else {
    playerRef.value.pause()
  }
}

onMounted(async () => {
  if (!playerRef.value) return

  const shaka = await import('shaka-player')

  shaka.default.polyfill.installAll()

  if (shaka.default.Player.isBrowserSupported()) {
    const shakaPlayer = new shaka.default.Player()

    await shakaPlayer.attach(playerRef.value)

    shakaPlayer.addEventListener('loaded', () => {
      isLoaded.value = true
    })

    shakaPlayer
      .getNetworkingEngine()
      ?.registerRequestFilter((_: unknown, request: { uris: string[] }) => {
        const url = new URL(request.uris[0])
        url.searchParams.set(
          'Authorization',
          `Bearer ${getTokens().accessToken}`
        )
        request.uris[0] = url.toString()
      })

    await shakaPlayer.load(props.url)
  }
})
</script>
